"use client";

import { useState } from "react";
import { useForm } from "react-hook-form";

import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Field,
  FieldError,
  FieldLabel,
  Input,
} from "@stocks/ui";
import type { CreateCustomerRequest, CustomerResponse } from "@stocks/shared";

import { useCreateCustomer } from "../model/useCreateCustomer";

type Values = Required<CreateCustomerRequest>;
const EMPTY: Values = {
  name: "",
  contactName: "",
  phone: "",
  email: "",
  address: "",
  memo: "",
};

/** 고객 신규 등록 다이얼로그. 등록되면 onCreated 로 새 고객을 돌려준다. */
export const CreateCustomerDialog = ({
  onCreated,
}: {
  onCreated?: (customer: CustomerResponse) => void;
}) => {
  const [open, setOpen] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Values>({ defaultValues: EMPTY });
  const create = useCreateCustomer();

  const onSubmit = async (v: Values) => {
    const body: CreateCustomerRequest = { name: v.name.trim() };
    for (const key of ["contactName", "phone", "email", "address", "memo"] as const) {
      const value = v[key].trim();
      if (value) body[key] = value;
    }
    const res = await create.mutateAsync(body);
    if (res.data.data) onCreated?.(res.data.data);
    reset(EMPTY);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type='button' variant='outline'>
          신규 고객 등록
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>신규 고객 등록</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            // 발주 폼 안에서 열려도 상위 form 이 제출되지 않도록
            e.stopPropagation();
            handleSubmit(onSubmit)(e);
          }}
          className='flex flex-col gap-3'
        >
          <Field>
            <FieldLabel htmlFor='customer-name'>고객명</FieldLabel>
            <Input
              id='customer-name'
              {...register("name", { required: "고객명을 입력하세요." })}
            />
            {errors.name && <FieldError>{errors.name.message}</FieldError>}
          </Field>
          <Field>
            <FieldLabel htmlFor='customer-contact'>담당자</FieldLabel>
            <Input id='customer-contact' {...register("contactName")} />
          </Field>
          <div className='grid gap-3 sm:grid-cols-2'>
            <Field>
              <FieldLabel htmlFor='customer-phone'>연락처</FieldLabel>
              <Input id='customer-phone' {...register("phone")} />
            </Field>
            <Field>
              <FieldLabel htmlFor='customer-email'>이메일</FieldLabel>
              <Input id='customer-email' type='email' {...register("email")} />
            </Field>
          </div>
          <Field>
            <FieldLabel htmlFor='customer-address'>주소</FieldLabel>
            <Input id='customer-address' {...register("address")} />
          </Field>
          <Field>
            <FieldLabel htmlFor='customer-memo'>메모</FieldLabel>
            <Input id='customer-memo' {...register("memo")} />
          </Field>
          <Button type='submit' disabled={create.isPending}>
            {create.isPending ? "등록 중…" : "등록"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
